import {
    createEntityAdapter,
    createSelector,
    createSlice,
    EntityState,
} from "@reduxjs/toolkit";

import { ActionAgainstPerpetrator } from "../../../api/actions_against_perpetrators";
import { RootState } from "../../../app/store";
import { OptionNameValuePair } from "../../../lib/interfaces/types";
import { getStakeholderRelationships, getStakeholders } from "../../stakeholder/logic/stakeholderSlice";
import {
    fetchActionsAgainstPerpetratorsThunk,
    upsertActionsAgainstPerpetratorsThunk,
} from "./ActionsAgainstPerpetratorsThunk";
import { isCreditAccountStakeholdersRevampEnabled } from "../../configs/logic/Slice";
import { selectPartiesWithRecommendedAction, selectSCNParties } from "../../partiesinvolved/logic/PartiesInvolvedSelectors";
import { convertStakeholdersToParties } from "../../stakeholder/logic/util";

const actionsAgainstPerpetratorsAdapter = createEntityAdapter<
    ActionAgainstPerpetrator,
    string
>({
    selectId: (action) => action.stakeholder_id,
});

interface ActionsAgainstPerpetratorsState {
    actions: EntityState<ActionAgainstPerpetrator, string>;
    status: "idle" | "loading" | "succeeded" | "failed";
    error: string | null;
    caseId: string | null;
}

const initialState: ActionsAgainstPerpetratorsState = {
    actions: actionsAgainstPerpetratorsAdapter.getInitialState(),
    status: "idle",
    error: null,
    caseId: null,
};

const actionsAgainstPerpetratorsSlice = createSlice({
    name: "actionsAgainstPerpetrators",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        // Fetch
        builder.addCase(fetchActionsAgainstPerpetratorsThunk.pending, (state) => {
            actionsAgainstPerpetratorsAdapter.removeAll(state.actions);
            state.status = "loading";
            state.error = null;
        });

        builder.addCase(
            fetchActionsAgainstPerpetratorsThunk.fulfilled,
            (state, action) => {
                actionsAgainstPerpetratorsAdapter.setAll(
                    state.actions,
                    action.payload.actions_against_perpetrators ?? []
                );
                state.status = "succeeded";
                state.error = null;
                state.caseId = action.meta.arg.caseId;
            }
        );

        builder.addCase(
            fetchActionsAgainstPerpetratorsThunk.rejected,
            (state, action) => {
                state.status = "failed";
                state.error = action.payload ?? "Failed to fetch actions against perpetrators";
            }
        );

        // Upsert
        builder.addCase(
            upsertActionsAgainstPerpetratorsThunk.fulfilled,
            (state, action) => {
                // PUT returns only a message, so mirror what we sent. No actions
                // means deleted - drop it, else re-adding lands in the old slot.
                const sentRows = action.meta.arg.request.actions_against_perpetrators;

                actionsAgainstPerpetratorsAdapter.removeMany(
                    state.actions,
                    sentRows
                        .filter((row) => !hasRecommendedActions(row.recommended_actions))
                        .map((row) => row.stakeholder_id)
                );

                actionsAgainstPerpetratorsAdapter.upsertMany(
                    state.actions,
                    sentRows.filter((row) =>
                        hasRecommendedActions(row.recommended_actions)
                    )
                );
            }
        );
    },
});

export const actionsAgainstPerpetratorsReducer =
    actionsAgainstPerpetratorsSlice.reducer;

const selectActionsAgainstPerpetratorsState = (
    state: RootState
): ActionsAgainstPerpetratorsState =>
    state.actionsAgainstPerpetratorsData ?? initialState;

const actionsSelectors = actionsAgainstPerpetratorsAdapter.getSelectors<RootState>(
    (state) => selectActionsAgainstPerpetratorsState(state).actions,
);

/** Raw endpoint rows; only this module builds on them. */
const selectPerpetratorActions = actionsSelectors.selectAll;

export const selectPerpetratorActionsStatus = (state: RootState) =>
    selectActionsAgainstPerpetratorsState(state).status;

export const selectPerpetratorActionsError = (state: RootState) =>
    selectActionsAgainstPerpetratorsState(state).error;

const hasRecommendedActions = (recommendedActions?: string[] | null) =>
    (recommendedActions?.length ?? 0) > 0;

/** Table rows. A row with no actions was deleted, so it is dropped here. */
export const selectRecordedPerpetratorActions = createSelector(
    [selectPerpetratorActions],
    (perpetratorActions) =>
        perpetratorActions.filter((action) =>
            hasRecommendedActions(action.recommended_actions)
        )
);

/** Options for the revamp table's "Party Involved" dropdown. */
export const selectInvolvedPartyOptions = createSelector(
    [getStakeholders, selectRecordedPerpetratorActions],
    (stakeholders, recordedActions): OptionNameValuePair<string>[] => {
        const stakeholderIdsAlreadyActioned = new Set(
            recordedActions.map((action) => action.stakeholder_id)
        );

        const partyOptions: OptionNameValuePair<string>[] = stakeholders
            .filter((stakeholder) => stakeholder.is_potential_perpetrator)
            .map((stakeholder) => ({
                name: stakeholder.name,
                value: stakeholder.stakeholder_id,
                disabled: stakeholderIdsAlreadyActioned.has(stakeholder.stakeholder_id),
            }));

        const listedStakeholderIds = new Set(partyOptions.map((option) => option.value));

        recordedActions.forEach((action) => {
            if (listedStakeholderIds.has(action.stakeholder_id)) return;

            const stakeholderName = stakeholders.find(
                (stakeholder) => stakeholder.stakeholder_id === action.stakeholder_id
            )?.name;

            if (!stakeholderName) return;

            listedStakeholderIds.add(action.stakeholder_id);
            partyOptions.push({
                name: stakeholderName,
                value: action.stakeholder_id,
                disabled: true,
            });
        });

        return partyOptions;
    }
);

export const selectStakeholdersWithActions = createSelector(
    [selectRecordedPerpetratorActions, getStakeholders],
    (recordedActions, stakeholders) => {
        return stakeholders.filter((stakeholder) => (recordedActions.find((action) => action.stakeholder_id === stakeholder.stakeholder_id) !== undefined))
    }
)

export const selectStakeholdersWithSCNAction = createSelector(
    [selectRecordedPerpetratorActions, getStakeholders],
    (recordedActions, stakeholders) => {
        return stakeholders.filter(
            (stakeholder) => (recordedActions.find(
                (action) => action.stakeholder_id === stakeholder.stakeholder_id && 
                    action.recommended_actions?.includes("Issue Show Cause Notice")
                ) !== undefined
            )
        )
    }
)

export const selectPartiesWithActions = createSelector(
    [isCreditAccountStakeholdersRevampEnabled, selectStakeholdersWithActions, getStakeholderRelationships, selectPartiesWithRecommendedAction],
    (isRevampEnabled, stakeholdersWithActions, relationships, partiesWithActions) => {
        if(isRevampEnabled){
            return convertStakeholdersToParties(stakeholdersWithActions, relationships);
        } else {
            return partiesWithActions;
        }
    }
)

export const selectPartiesWithSCNAction = createSelector(
    [isCreditAccountStakeholdersRevampEnabled, selectStakeholdersWithSCNAction, getStakeholderRelationships, selectSCNParties],
    (isRevampEnabled, stakeholders, relationships, scnParties) => {
        if(isRevampEnabled){
            return convertStakeholdersToParties(stakeholders, relationships);
        } else {
            return scnParties;
        }
    }
)
